// TenantProvider — publishes the viewer's current org + workspace and their
// seats (decision workspace-tenancy). Mounted inside AuthProvider.
//
// On sign-in it pings GET /tenant/me so the backend seats a first-time user
// (shared/tenant.js ensureSeat: pending invitations in any org, else the
// default org); after that everything is
// live Firestore/pg reads of the viewer's own seat rows (rules allow
// self-reads via the collection-group clauses). The chosen workspace is
// remembered per org in localStorage.
import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from './AuthContext';
import { getIdToken } from './data/authPlane';
import { kitEnv } from './data/kitEnv.js';
import {
  TenantContext, useMySeats, useEntityList, setCurrentTenant, isPrivileged, workspaceRole,
} from './data/entities';

const API = kitEnv('UPLOAD_API', process.env.REACT_APP_UPLOAD_API || '');
const ORG_KEY = 'nv.org';
const wsKey = (orgId) => `nv.ws.${orgId}`;

function WorkspaceFeed({ orgId, onDocs }) {
  const { docs } = useEntityList('workspace', { org: orgId }, { orderBy: 'createdAt', dir: 'asc' });
  useEffect(() => { onDocs(docs); }, [docs, onDocs]);
  return null;
}

export function TenantProvider({ children }) {
  const { user } = useAuth();
  const uid = user && !user.isAnonymous ? user.uid : null;
  const { orgs, wsRoles, loaded } = useMySeats(uid);
  // ?org=<id> on the landing URL (the nventr.io SSO ticket names the org the
  // caller was seated in) wins once and is remembered like a manual pick.
  const [orgPick, setOrgPick] = useState(() => {
    const fromUrl = new URLSearchParams(window.location.search).get('org');
    if (fromUrl) { localStorage.setItem(ORG_KEY, fromUrl); return fromUrl; }
    return localStorage.getItem(ORG_KEY) || null;
  });
  const [wsPick, setWsPick] = useState(null);
  const [workspaces, setWorkspaces] = useState(null);

  // Seat a first-time user (server-side, admin SDK). Fire-and-forget.
  useEffect(() => {
    if (!uid || !API) return;
    getIdToken().then((token) => token && fetch(`${API}/tenant/me`, {
      headers: { Authorization: `Bearer ${token}` },
    })).catch(() => {});
  }, [uid]);

  const orgIds = Object.keys(orgs);
  const orgId = orgIds.includes(orgPick) ? orgPick : orgIds[0] || null;
  const role = orgId ? orgs[orgId] : null;

  useEffect(() => {
    if (!orgId) { setWsPick(null); return; }
    setWsPick(localStorage.getItem(wsKey(orgId)) || null);
  }, [orgId]);

  const myWsRoles = useMemo(() => {
    const out = {};
    for (const [k, r] of Object.entries(wsRoles)) {
      const [o, w] = k.split('/');
      if (o === orgId) out[w] = r;
    }
    return out;
  }, [wsRoles, orgId]);
  const workspaceIds = Object.keys(myWsRoles);
  const visible = useMemo(() => {
    if (!workspaces) return [];
    return isPrivileged(role) ? workspaces : workspaces.filter((w) => myWsRoles[w.id]);
  }, [workspaces, role, myWsRoles]);
  const workspace = wsPick && visible.some((w) => w.id === wsPick) ? wsPick : null;

  useEffect(() => { setCurrentTenant(orgId, workspace); }, [orgId, workspace]);

  const value = useMemo(() => ({
    uid,
    orgId,
    role,
    orgs: orgIds.map((id) => ({ id, role: orgs[id] })),
    workspace,
    workspaceIds,
    wsRoles: myWsRoles,
    workspaces: visible,
    wsRole: workspaceRole(role, myWsRoles, workspace),
    loaded,
    setOrg: (id) => { localStorage.setItem(ORG_KEY, id || ''); setOrgPick(id || null); },
    setWorkspace: (id) => {
      if (orgId) localStorage.setItem(wsKey(orgId), id || '');
      setWsPick(id || null);
    },
  }), [uid, orgId, role, orgIds.join(','), workspace, workspaceIds.join(','), myWsRoles, visible, loaded]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <TenantContext.Provider value={value}>
      {orgId ? <WorkspaceFeed orgId={orgId} onDocs={setWorkspaces} /> : null}
      {children}
    </TenantContext.Provider>
  );
}
